import { useQuery } from "@apollo/client";
import { GET_PERSON_DUES_BALANCE_QUERY, GET_PERSON_DUES_ORDERS_QUERY } from "@/pages/people/[pgaId]/person/queries";
import {
  DuesOrdersData,
  ACTIVE_DUES_ORDER_STATUSES,
  findMatchingDuesOrder,
  buildDuesMetric,
  buildBalanceOnlyDuesMetric
} from "@/lib/duesHelpers";
import { hasDuesPayingProgram } from "@/pages/people/_utils";
import { Person } from "@/types";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";

const DUES_METRIC_PLACEHOLDER: MetricCardType<Path> = {
  label: "Dues Status (Balance)",
  value: "",
  iconName: "paid",
  loading: true
};

export function useDuesStatus(
  person: Person,
  options?: {
    skip?: boolean;
    skipOrders?: boolean;
  }
) {
  const hasDuesProgram = hasDuesPayingProgram(person);
  const skipBalance = options?.skip || !person.pgaId || !hasDuesProgram;
  const skipOrders = skipBalance || options?.skipOrders;

  const { data: balanceData, loading: balanceLoading } = useQuery(GET_PERSON_DUES_BALANCE_QUERY, {
    variables: { pgaId: person.pgaId || "" },
    skip: skipBalance
  });

  const { data: duesOrdersData, loading: ordersLoading } = useQuery<DuesOrdersData>(GET_PERSON_DUES_ORDERS_QUERY, {
    variables: {
      pgaId: person.pgaId || "",
      statuses: ACTIVE_DUES_ORDER_STATUSES
    },
    skip: skipOrders
  });

  const duesBalance = balanceData?.person?.duesBalance ?? 0;
  const allOrders = duesOrdersData?.duesOrders?.nodes || [];
  const loading = balanceLoading || (!skipOrders && ordersLoading);

  // Find dues order that matches the person's current balance
  const matchingOrder = findMatchingDuesOrder(allOrders, person.pgaId || "", duesBalance);

  const duesMetric = skipBalance
    ? undefined
    : matchingOrder
      ? buildDuesMetric(matchingOrder, person.pgaId, allOrders)
      : options?.skipOrders && !loading
        ? buildBalanceOnlyDuesMetric(duesBalance)
        : loading
          ? DUES_METRIC_PLACEHOLDER
          : null;

  return {
    duesMetric,
    matchingOrder,
    hasMatchingOrder: !!matchingOrder,
    loading
  };
}
